// @flow

import { createSelector } from 'reselect';

import {
  type AccessControlReducer,
  type AccessResource,
  type AccessResourcePermissions
} from '../types/accessControlTypes';
import { createInitialState } from '../reducers/AccessControlReducer';
import { hasAccess } from '../../common/support/utils/accessControlHelpers';
import { LOADING_STATUS as LoadingStatus } from '../support/constants';

/**
 * Return AccessControl reducer state from global redux state.
 */
export function getState(state: any): AccessControlReducer {
  return (state && state.accessControl) || createInitialState();
}

/**
 * Get loading status of Access Control
 */
export const getLoadingStatus: (state: any) => number = createSelector(
  getState,
  state => state.loadingStatus
);

export const isAccessControlLoading: (state: any) => number = createSelector(
  getState,
  state => state.loadingStatus < 2
);

export const getUserPermissionsLoadingStatus: (
  state: any
) => number = createSelector(
  getState,
  state => state.getUserPermissionsLoadingStatus
);

export const getDefaultRolesLoadingStatus: (
  state: any
) => number = createSelector(
  getState,
  state => state.defaultRolesLoadingStatus
);

export const getError: (state: any) => ?Object = createSelector(
  getState,
  state => state.error
);

/**
 * Get all loaded roles for current user.
 */
export const getRoles: (
  state: any
) => AccessResourcePermissions = createSelector(
  getState,
  state => state.data
);

/**
 * Get default roles for the current user, if found.
 */
export const getDefaultRoles: (
  state: any
) => ?AccessResourcePermissions = createSelector(
  getState,
  state => state.defaultRoles
);

export const getAgencyTaggedData: (
  state: any
) => ?AccessResource = createSelector(
  getRoles,
  state => state.agency_tagged_data
);

export const getAdNetworkPermission: (state: any) => ?number = createSelector(
  getRoles,
  state => state.ad_networks.permission
);

export const isAuthorized: (
  state: any,
  access?: { permission: number, accessResources: string[] }
) => ?boolean = createSelector(
  getRoles,
  (_, access) => access,
  (roles, access) => {
    return (
      access && hasAccess(access.accessResources, access.permission, roles)
    );
  }
);

export const isLoading: (state: any) => Boolean = createSelector(
  getState,
  state => {
    if (!state.loadingStatus) {
      return true;
    }

    return Boolean(state.loadingStatus === LoadingStatus.LOADING);
  }
);
